import { useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";

const LISTEN_LABEL = {
  EN: "Listen to this fable",
  ES: "Escucha esta fábula",
  DE: "Höre diese Fabel",
  FR: "Écoute cette fable",
  IT: "Ascolta questa fiaba",
  PT: "Ouve esta fábula",
};

const fmt = (s) => {
  if (!isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

export default function StoryAudio({ bookId, lang, langName }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [dur, setDur] = useState(0);
  const src = `/audio/${bookId}-${lang}.mp3`;

  useEffect(() => {
    setPlaying(false);
    setTime(0);
    setDur(0);
    const el = ref.current;
    if (el) {
      el.pause();
      el.load();
    }
  }, [src]);

  useEffect(() => () => ref.current?.pause(), []);

  const toggle = async () => {
    const el = ref.current;
    if (!el) return;
    if (playing) {
      el.pause();
      setPlaying(false);
    } else {
      try {
        await el.play();
        setPlaying(true);
      } catch {
        setPlaying(false);
      }
    }
  };

  const progress = dur > 0 ? (time / dur) * 100 : 0;

  return (
    <div
      className="mx-auto mt-6 flex w-full max-w-md items-center gap-4 rounded-full border border-gold/30 bg-emerald-ink/60 px-4 py-3"
      data-testid="story-audio-player"
    >
      <audio
        ref={ref}
        src={src}
        preload="metadata"
        onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDur(e.currentTarget.duration)}
        onEnded={() => setPlaying(false)}
        onPause={() => setPlaying(false)}
      />
      <button
        onClick={toggle}
        data-testid="story-audio-toggle"
        aria-label={playing ? "Pause story narration" : `Play story narration in ${langName}`}
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gold text-emerald-ink transition-transform duration-300 hover:scale-105"
      >
        {playing ? (
          <Pause className="h-4 w-4 fill-current" aria-hidden="true" />
        ) : (
          <Play className="ml-0.5 h-4 w-4 fill-current" aria-hidden="true" />
        )}
      </button>
      <div className="min-w-0 flex-1">
        <p className="truncate font-mono text-[10px] uppercase tracking-[0.2em] text-gold/85">
          {LISTEN_LABEL[lang]}
        </p>
        <div
          className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-cream/15"
          role="progressbar"
          aria-valuenow={Math.round(progress)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="h-full rounded-full bg-gold transition-[width] duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
      <span className="shrink-0 font-mono text-[10px] text-cream/55">
        {fmt(time)} / {fmt(dur)}
      </span>
    </div>
  );
}
